import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { SERVICES } from "@/data/content";

export default function Services() {
  return (
    <div data-testid="services-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 grid-bg radial-fade opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <p className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#00D4FF] mb-4">
            Capabilities
          </p>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-white max-w-3xl leading-[1.05]">
            Services that move from <span className="text-gradient-cyan">idea to impact.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            A full-stack engineering studio — covering AI, data, cloud,
            product, and platform engineering for teams that are serious about
            shipping.
          </p>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-8">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const reverse = i % 2 === 1;
            return (
              <motion.article
                key={s.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                data-testid={`service-detail-${s.slug}`}
                className="grid lg:grid-cols-12 gap-6 p-6 md:p-10 rounded-3xl bg-[#0A1128] border border-white/10 hover-glow"
              >
                <div className={`lg:col-span-4 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="w-14 h-14 rounded-xl bg-[#00D4FF]/10 border border-[#00D4FF]/30 flex items-center justify-center mb-5">
                    <Icon size={26} className="text-[#00D4FF]" />
                  </div>
                  <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#00D4FF] mb-2">
                    0{i + 1}
                  </p>
                  <h2 className="font-display text-3xl font-bold text-white">
                    {s.title}
                  </h2>
                </div>
                <div className={`lg:col-span-8 space-y-6 ${reverse ? "lg:order-1" : ""}`}>
                  <p className="text-slate-300 leading-relaxed text-base">
                    {s.long}
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {s.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 text-sm text-slate-200">
                        <Check size={16} className="text-[#00D4FF] mt-0.5 shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                  <div>
                    <Link
                      to="/contact"
                      data-testid={`service-cta-${s.slug}`}
                      className="inline-flex items-center gap-2 text-[#00D4FF] hover:text-white transition text-sm font-semibold"
                    >
                      Discuss this with us <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-white">
            Not sure where to start?
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
            Tell us about your goals. We'll come back with a clear, opinionated
            recommendation — and an honest plan.
          </p>
          <Link
            to="/contact"
            data-testid="services-page-cta"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#00D4FF] text-[#050B14] font-semibold hover:bg-[#33DDFF] transition"
          >
            Schedule a call <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
